import type { Control, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import type { Renter } from '../../utils/types/renter.interface';

import { useEffect, useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Checkbox, FormControlLabel, FormHelperText, Stack, Typography } from '@mui/material';

import { InfoBanner } from '@/components/InfoBanner';

import { useSplitFeatureFlag } from '@/hooks/useSplitFeatureFlag';
import { useCoverageRules } from '@/shared/hooks';
import { SplitFeatureFlag } from '@/utils/split/splitConfig';
import GroupRadioButton, {
  GroupRadioButtonSkeleton,
} from '../../components/input/GroupRadioButton';
import {
  staticInstitutionalScreeningResultsOptions,
  staticSmbScreeningResultsOptions,
} from './ScreeningResults.options';
import { resolveInstitutionalCoverageOptions } from './utils/coverage.util';

interface ScreeningResultsProps {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  watch: UseFormWatch<Renter>;
  setValue: UseFormSetValue<Renter>;
  isSmbInvites: boolean;
}

export const ScreeningResults = ({
  control,
  errors,
  watch,
  setValue,
  isSmbInvites,
}: ScreeningResultsProps) => {
  const customCoverage = watch('customCoverage');
  const { isFeatureFlagEnabled: isDynamicCoveragesFeatureFlagEnabled } = useSplitFeatureFlag(
    SplitFeatureFlag.OTTERS_4472_DYNAMIC_COVERAGES_INVITE,
  );

  const address = useWatch({ control, name: 'address' });
  const screeningResult = useWatch({ control, name: 'approvalStatus' });

  const addressSelected = Boolean(address?.monolith_building_id);

  const isDynamicCoverageRulesEnabled = Boolean(
    isDynamicCoveragesFeatureFlagEnabled && address?.property_id && !isSmbInvites,
  );

  const { data: coverageRulesFromApi, isLoading } = useCoverageRules(
    address?.property_id ?? '',
    isDynamicCoverageRulesEnabled,
  );

  const options = useMemo(
    () =>
      isSmbInvites
        ? staticSmbScreeningResultsOptions
        : resolveInstitutionalCoverageOptions(
            staticInstitutionalScreeningResultsOptions,
            coverageRulesFromApi,
          ),
    [coverageRulesFromApi, isSmbInvites],
  );

  useEffect(() => {
    if (
      !isSmbInvites &&
      !isLoading &&
      screeningResult &&
      options.some((opt) => opt.value === screeningResult && opt.disabled)
    ) {
      setValue('approvalStatus', '', { shouldValidate: true });
    }
  }, [isSmbInvites, isLoading, screeningResult, options, setValue]);

  const showSkeleton = isLoading && !isSmbInvites && addressSelected;
  const isLocked = isDynamicCoveragesFeatureFlagEnabled && !addressSelected && !isSmbInvites;

  return (
    <>
      <Stack gap={4}>
        <Stack gap={1}>
          <Typography variant="h5" component="h2">
            {isSmbInvites ? 'Select your coverage' : 'Screening result'}
          </Typography>
          <Typography>
            {isSmbInvites
              ? `Based on your review of this renter at this time, please select or customize what level of
        coverage you require for your unit.*`
              : `Based on your review of this renter at this time, are they approved, approved with conditions, or denied? This can be changed later, but it will impact the quoted premium.`}
          </Typography>
          {isLocked && (
            <InfoBanner icon={<LockOutlinedIcon fontSize="inherit" />}>
              Please complete the lease information above first
            </InfoBanner>
          )}
        </Stack>
        <Stack gap={1.5}>
          {!isSmbInvites && <Typography>Select one *</Typography>}
          <Box>
            {showSkeleton ? (
              <GroupRadioButtonSkeleton
                count={staticInstitutionalScreeningResultsOptions.length}
                dataTestId="screening-results-skeleton"
              />
            ) : (
              <Controller
                control={control}
                name="approvalStatus"
                render={({ field }) => (
                  <GroupRadioButton
                    id="approvalStatus"
                    dataTestId="approvalStatus"
                    options={options}
                    field={field}
                    disabled={isLocked || (isSmbInvites && !!customCoverage)}
                  />
                )}
              />
            )}
            {errors.approvalStatus && (
              <FormHelperText error={true}>{errors.approvalStatus.message}</FormHelperText>
            )}
          </Box>
        </Stack>
      </Stack>
      {isSmbInvites && (
        <>
          <Box my={4}>
            <Controller
              name="customCoverage"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox {...field} checked={field.value} color="primary" sx={{ mx: 1 }} />
                  }
                  label="I want to customize my own coverage"
                />
              )}
            />
          </Box>
          <Box mb={4}>
            <Typography variant="helpertext">
              * Policy price paid by the renter is determined by gross rent, the length of coverage,
              and a renter's risk profile, decided by the landlord.
            </Typography>{' '}
          </Box>
        </>
      )}
    </>
  );
};
